import React from 'react'
import PropTypes from 'prop-types'
import {defineMessage, FormattedMessage} from 'react-intl'
import {Alert, Box, Flex, Text} from '@chakra-ui/react'
import {CloseIcon, UnavailableCartIcon, WarningIcon} from '../../../components/icons'
import {useD2BCustomer} from '../../../hooks/use-d2b-customer'

const UnavailableIcon = () => (
    <Flex position="relative">
        <UnavailableCartIcon boxSize={4} />
        <Box
            {...{
                position: 'absolute',
                bg: 'stokkeCore.black',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: 'full',

                top: '-0.18rem',
                right: '-0.21rem',
                width: '0.5rem',
                height: '0.5rem',
                padding: '2px'
            }}
        >
            <CloseIcon color="stokkeCore.white" boxSize={1} />
        </Box>
    </Flex>
)

const VARIANTS = {
    overstock: {
        alert: {
            bg: 'stokkePink.mid',
            color: 'stokkePink.dark',
            status: 'error'
        },
        icon: WarningIcon,
        message: defineMessage({
            defaultMessage: 'Please adjust your quantity as we only have {quantity} in stock.',
            id: 'product.alert.over_stock_quantity'
        }),
        messageD2B: defineMessage({
            id: 'product.alert.d2b.max_order_quantity_count',
            defaultMessage:
                'Maximum {quantity, plural, one {# item} other {# items}} in stock. Please contact customer service to place an order for a higher quantity.'
        })
    },
    overquantity: {
        alert: {
            bg: 'stokkePink.mid',
            color: 'stokkePink.dark',
            status: 'error'
        },
        icon: WarningIcon,
        message: defineMessage({
            defaultMessage: 'Maximum of {quantity} can be added.',
            id: 'product.alert.b2c.max_order_quantity_count'
        }),
        messageD2B: defineMessage({
            id: 'product.alert.d2b.max_order_quantity_count',
            defaultMessage:
                'Maximum {quantity, plural, one {# item} other {# items}} in stock. Please contact customer service to place an order for a higher quantity.'
        })
    },
    unavailable: {
        alert: {
            bg: 'stokkeGray.mid',
            color: 'stokkeCore.black',
            status: 'error'
        },
        icon: UnavailableIcon,
        message: defineMessage({
            defaultMessage: 'This item is currently unavailable.',
            id: 'cart.info.unavailable'
        })
    },
    incompatible: {
        alert: {
            bg: 'stokkeBeige.mid',
            color: 'stokkeBeige.accessibleText',
            status: 'warning'
        },
        icon: WarningIcon,
        message: defineMessage({
            defaultMessage: '{masterProductName} is not compatible with {incompatibleProductName}.',
            id: 'cart.info.incompatible_product_text'
        })
    }
}

const CartItemMessage = ({variant, icon: Icon = VARIANTS[variant].icon, values}) => {
    const {enabled: isD2BCustomer} = useD2BCustomer()
    return (
        <Alert variant="solid" paddingInline={4} paddingBlock={3} {...VARIANTS[variant].alert}>
            <Flex gap={{base: 2, xl: 3}} alignItems="flex-start">
                <Icon boxSize={4} />
                <Text textStyle={{base: 'Regular Extra Small', xl: 'Regular Small'}}>
                    <FormattedMessage
                        {...(isD2BCustomer
                            ? VARIANTS[variant].messageD2B ?? VARIANTS[variant].message
                            : VARIANTS[variant].message)}
                        values={values}
                    />
                </Text>
            </Flex>
        </Alert>
    )
}

CartItemMessage.propTypes = {
    variant: PropTypes.oneOf(Object.keys(VARIANTS)).isRequired,
    icon: PropTypes.elementType,
    values: PropTypes.object
}

export default CartItemMessage
